/**
 * GlobalDisclaimerEditor · v2.7.45+E7 (2026-02-22)
 * ═════════════════════════════════════════════════════════════════════
 * Spec §3 — admin-only editor for the two corporate disclaimer blocks
 * that the renderer auto-appends to every signature.
 *
 * Non-admins still see this block (so they understand what gets
 * appended below their signatures) but with READ-ONLY copy + a clear
 * "locked" indicator. Only `role === 'admin'` can submit.
 */
import { useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Shield, Lock, Save, Loader2, Clock, User as UserIcon } from "lucide-react";

// v2.7.45+E11 · Format the audit timestamp as a compact human-readable
// string (e.g. "Feb 22, 2026 · 3:14 PM"). Falls back to the raw ISO if
// the browser can't parse it for some reason. Locale follows the browser.
function fmtAuditTs(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function GlobalDisclaimerEditor({ adminRole }) {
  const isMasterAdmin = adminRole === "admin";
  const [d1, setD1] = useState("");
  const [d2, setD2] = useState("");
  const [audit, setAudit] = useState({ updated_at: null, updated_by: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/email/disclaimer");
        setD1(data.disclaimer1 || "");
        setD2(data.disclaimer2 || "");
        setAudit({ updated_at: data.updated_at || null, updated_by: data.updated_by || null });
      } catch (e) {
        // Silent — non-admin who lost access just sees defaults blank
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    if (!isMasterAdmin) return;
    setSaving(true);
    try {
      await adminApi.put("/admin/email/disclaimer", { disclaimer1: d1, disclaimer2: d2 });
      toast.success("Disclaimer saved · every signature re-rendered");
      // Refresh audit row from server so the displayed timestamp matches
      // the persisted value (and reflects the just-saved user).
      try {
        const { data } = await adminApi.get("/admin/email/disclaimer");
        setAudit({ updated_at: data.updated_at || null, updated_by: data.updated_by || null });
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border border-amber-200 dark:border-amber-800 rounded p-3 bg-amber-50/60 dark:bg-amber-900/10 space-y-3"
      data-testid="global-disclaimer-editor"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-amber-600 dark:text-amber-400"/>
          <div className="text-sm font-extrabold uppercase tracking-widest text-slate-900 dark:text-zinc-100">
            Global Corporate Disclaimer
          </div>
        </div>
        {!isMasterAdmin && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-slate-200 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300">
            <Lock className="h-3 w-3"/>Admin-only · Read-only
          </span>
        )}
      </div>
      <div className="text-[11px] text-slate-600 dark:text-zinc-400">
        These two blocks are auto-appended to <b>every</b> signature in centered 9-px light-gray text. Users cannot edit, hide, or move them from their personal settings.
      </div>

      <div className="space-y-2">
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">Disclaimer 1</label>
          <Textarea
            value={d1}
            onChange={(e) => setD1(e.target.value)}
            rows={2}
            disabled={!isMasterAdmin || loading}
            placeholder="Copyright © 2026 Buller Taxi Services, LLC®. | Bison Software, Inc™. All rights reserved."
            data-testid="disclaimer-1-input"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">Disclaimer 2</label>
          <Textarea
            value={d2}
            onChange={(e) => setD2(e.target.value)}
            rows={2}
            disabled={!isMasterAdmin || loading}
            placeholder="This platform is property of Buller Taxi Services LLC-LP General Partnership Company."
            data-testid="disclaimer-2-input"
          />
        </div>
      </div>

      {isMasterAdmin && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <AuditRow audit={audit}/>
          <Button
            onClick={save}
            disabled={saving || loading}
            className="bg-amber-600 hover:bg-amber-500 text-white font-bold h-8 text-xs gap-1.5"
            data-testid="disclaimer-save-btn"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin"/> : <Save className="h-3 w-3"/>}
            Save &amp; recompile signatures
          </Button>
        </div>
      )}
      {!isMasterAdmin && <AuditRow audit={audit}/>}

      {/* Preview */}
      {(d1 || d2) && (
        <div className="border-t border-amber-200 dark:border-amber-800/40 pt-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 mb-1.5">
            Preview · how disclaimers render
          </div>
          <div className="bg-white border border-slate-200 rounded p-3">
            <div style={{ borderTop: "1px solid #d1d5db", paddingTop: 6, textAlign: "center" }}>
              <div style={{ font: "400 9px Inter, Arial, sans-serif", color: "#9ca3af", lineHeight: 1.4 }}>{d1}</div>
              <div style={{ font: "400 9px Inter, Arial, sans-serif", color: "#9ca3af", marginTop: 2, lineHeight: 1.4 }}>{d2}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// AuditRow · v2.7.45+E11 (Feb-2026)
// Compact legal-compliance paper trail — "Last updated · {when} · by
// {who}". When the doc is freshly-seeded with no audit fields yet, we
// show a neutral "Never updated" line so admins know the record is
// still on factory defaults.
// ─────────────────────────────────────────────────────────────────────
function AuditRow({ audit }) {
  const when = fmtAuditTs(audit?.updated_at);
  const who  = audit?.updated_by;
  if (!when && !who) {
    return (
      <div
        className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500"
        data-testid="disclaimer-audit-row"
      >
        <Clock className="h-3 w-3"/>
        <span>Never updated · factory defaults active</span>
      </div>
    );
  }
  return (
    <div
      className="flex items-center gap-2 text-[10px] tracking-wide text-slate-600 dark:text-zinc-400 flex-wrap"
      data-testid="disclaimer-audit-row"
    >
      {when && (
        <span className="inline-flex items-center gap-1 font-semibold">
          <Clock className="h-3 w-3 text-slate-500 dark:text-zinc-500"/>
          <span data-testid="disclaimer-audit-when">Last updated&nbsp;·&nbsp;{when}</span>
        </span>
      )}
      {who && (
        <span className="inline-flex items-center gap-1 font-semibold">
          <span className="text-slate-300 dark:text-zinc-600">·</span>
          <UserIcon className="h-3 w-3 text-slate-500 dark:text-zinc-500"/>
          <span data-testid="disclaimer-audit-who">by&nbsp;{who}</span>
        </span>
      )}
    </div>
  );
}
